import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, Typography, TextField, Grid, Switch, FormControlLabel,
    LinearProgress, Breadcrumbs, Link, Stack, FormHelperText
} from '@mui/material';
import { Home, Description, PictureAsPdf, Article, TableChart, Download } from '@mui/icons-material';
import tenderApi from '../../../../services/tenderApi';
import ImageUploader from '../../common/ImageUploader';
import AnimateButton from '../../../AnimateButton';
import { BUTTON_CANCEL_COLOR, BUTTON_SAVE_COLOR } from '../../../common/color';
import Swal from 'sweetalert2';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { getImageUrl } from '../../../../services/api';

const TenderBody = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const isNew = id === 'new';
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        closingDate: new Date(),
        file: null,
        isActive: true
    });
    const [existingFileUrl, setExistingFileUrl] = useState('');
    const [existingFileName, setExistingFileName] = useState('');
    const [errors, setErrors] = useState({ 
        title: '', 
        description: '', 
        closingDate: '', 
        file: '' 
    });
    const [touched, setTouched] = useState({ 
        title: false, 
        description: false, 
        closingDate: false, 
        file: false 
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const getFileIcon = (fileName) => {
        if (!fileName) return <Description fontSize="large" color="primary" />;
        
        const extension = fileName.split('.').pop().toLowerCase();
        
        switch(extension) {
            case 'pdf':
                return <PictureAsPdf fontSize="large" color="error" />;
            case 'doc':
            case 'docx':
                return <Article fontSize="large" color="info" />;
            case 'xls':
            case 'xlsx':
                return <TableChart fontSize="large" color="success" />;
            default:
                return <Description fontSize="large" color="primary" />;
        }
    };

    const handleDownload = (fileUrl, fileName) => {
        if (!fileUrl) return;
        
        const downloadUrl = getImageUrl(fileUrl);
        const downloadFileName = fileName || fileUrl.split('/').pop();
        
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = downloadFileName;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        
        link.style.display = 'none';
        document.body.appendChild(link);
        
        link.click();
        
        setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        }, 100);
    };

    useEffect(() => {
        if (!isNew) {
            const fetchTender = async () => {
                try {
                    const tender = await tenderApi.getById(id);
                    setFormData({
                        title: tender.title || '',
                        description: tender.description || '',
                        closingDate: tender.closingDate ? new Date(tender.closingDate) : new Date(),
                        isActive: tender.isActive
                    });
    
                    if (tender.fileUrl) {
                        setExistingFileUrl(tender.fileUrl);
                        setExistingFileName(tender.fileUrl.split('/').pop());
                    }
                } catch (error) {
                    console.error('Fetch error:', error);
                    Swal.fire('Error', 'Failed to load tender', 'error');
                    navigate('/admin/tenders');
                } finally {
                    setLoading(false);
                }
            };
            fetchTender();
        } else {
            setLoading(false);
        }
    }, [id, isNew, navigate]);

    const validateField = (name, value) => {
        switch (name) {
            case 'title': 
                return value.trim() ? '' : 'Title is required';
            case 'description': 
                return value.trim() ? '' : 'Description is required';
            case 'closingDate': 
                return value ? '' : 'Closing date is required';
            case 'file': 
                return (isNew ? !value : (!value && !existingFileUrl)) 
                    ? 'Document is required' 
                    : '';
            default: 
                return '';
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (touched[name]) {
            setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
        }
    };

    const handleDateChange = (date) => {
        setFormData(prev => ({ ...prev, closingDate: date }));
        if (touched.closingDate) {
            setErrors(prev => ({ ...prev, closingDate: validateField('closingDate', date) }));
        }
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched(prev => ({ ...prev, [name]: true }));
        setErrors(prev => ({ ...prev, [name]: validateField(name, formData[name]) }));
    };

    const handleFileSelect = (file) => {
        if (!file) return;
        
        setFormData(prev => ({ ...prev, file }));
        setErrors(prev => ({ ...prev, file: '' }));
    };

    const handleSubmit = async () => {
        const newTouched = {
            title: true,
            description: true,
            closingDate: true,
            file: true
        };
        setTouched(newTouched);
        
        const newErrors = {
            title: validateField('title', formData.title),
            description: validateField('description', formData.description),
            closingDate: validateField('closingDate', formData.closingDate),
            file: validateField('file', formData.file)
        };
        setErrors(newErrors);
        
        if (Object.values(newErrors).some(error => error)) {
            return;
        }
      
        const confirm = await Swal.fire({
            title: 'Confirm',
            text: `Are you sure you want to ${isNew ? 'create' : 'update'} this tender?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: BUTTON_SAVE_COLOR,
            cancelButtonColor: BUTTON_CANCEL_COLOR
        });
      
        if (!confirm.isConfirmed) return;
      
        setIsSubmitting(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('title', formData.title);
            formDataToSend.append('description', formData.description);
            formDataToSend.append('closingDate', formData.closingDate.toISOString());
            formDataToSend.append('isActive', formData.isActive);
            
            if (formData.file) {
                formDataToSend.append('file', formData.file);
            }
      
            if (isNew) {
                await tenderApi.create(formDataToSend);
            } else {
                await tenderApi.update(id, formDataToSend);
            }
      
            Swal.fire('Success', `Tender ${isNew ? 'created' : 'updated'}`, 'success');
            navigate('/admin/tenders');
        } catch (error) {
            console.error('Error:', error);
            Swal.fire('Error', error.response?.data?.error || 'Operation failed', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) return <LinearProgress />;

    return (
        <Box sx={{ width: '100%', p: 3 }}>
            <Dialog open fullWidth maxWidth="md" onClose={() => navigate('/admin/tenders')}>
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between">
                        <Breadcrumbs>
                            <Link href="#" underline="hover" color="inherit">
                                <Home sx={{ mr: 0.5 }} /> Announcements
                            </Link>
                            <Link href="/admin/tenders" underline="hover" color="inherit">
                                Tenders
                            </Link>
                            <Typography color="text.primary">
                                {isNew ? 'Create' : 'Edit'}
                            </Typography>
                        </Breadcrumbs>
                    </Stack>
                </DialogTitle>
                
                <Divider />
                
                <DialogContent sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h5" gutterBottom>Tender Information</Typography>
                            
                            <TextField
                                required
                                fullWidth
                                label="Title"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                error={touched.title && Boolean(errors.title)}
                                helperText={touched.title && errors.title}
                                sx={{ mb: 3 }}
                                size="small"
                            />
                            
                            <TextField
                                required
                                fullWidth
                                label="Description"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                error={touched.description && Boolean(errors.description)}
                                helperText={touched.description && errors.description}
                                multiline
                                rows={4}
                                sx={{ mb: 3 }}
                                size="small"
                            />

                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DateTimePicker
                                    label="Closing Date"
                                    value={formData.closingDate}
                                    onChange={handleDateChange}
                                    minDateTime={new Date()}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            required
                                            size="small"
                                            sx={{ mb: 3 }}
                                            error={touched.closingDate && Boolean(errors.closingDate)}
                                            helperText={touched.closingDate && errors.closingDate}
                                            onBlur={() => {
                                                setTouched(prev => ({ ...prev, closingDate: true }));
                                                setErrors(prev => ({ ...prev, closingDate: validateField('closingDate', formData.closingDate) }));
                                            }}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                            
                            <FormControlLabel
                                control={<Switch checked={formData.isActive} onChange={(e) => 
                                    setFormData(prev => ({ ...prev, isActive: e.target.checked }))
                                } />}
                                label="Active Tender"
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <Typography variant="h5" gutterBottom>Document Upload</Typography>
                            
                            <Box sx={{ 
                                p: 2, 
                                mb: 3, 
                                border: '1px solid',
                                borderColor: errors.file && touched.file ? 'error.main' : 'divider',
                                borderRadius: 1
                            }}>
                                <ImageUploader 
                                    onFileSelect={handleFileSelect}
                                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                                    maxSize={10 * 1024 * 1024} // 10MB
                                />
                                {errors.file && touched.file && (
                                    <FormHelperText error>{errors.file}</FormHelperText>
                                )}
                                <Typography variant="caption" color="textSecondary">
                                    PDF, DOC, XLS. Max 10MB.
                                </Typography>
                            </Box>

                            <Typography variant="h5" gutterBottom>Current Document</Typography>
                            
                            <Box sx={{ 
                                p: 2, 
                                border: '1px solid', 
                                borderColor: 'divider',
                                borderRadius: 1,
                                minHeight: 100,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}>
                                {formData.file || existingFileUrl ? (
                                    <Box sx={{ 
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2
                                    }}>
                                        {getFileIcon(formData.file?.name || existingFileName)}
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body1" noWrap>
                                                {formData.file?.name || existingFileName}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {existingFileUrl && !formData.file ? 
                                                    'Existing document will be kept' : 
                                                    'New document will replace existing one'}
                                            </Typography>
                                            {existingFileUrl && (
                                                <Box sx={{ mt: 1 }}>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<Download />}
                                                        onClick={() => handleDownload(existingFileUrl, existingFileName)}
                                                    >
                                                        Download
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box textAlign="center">
                                        <Description color="disabled" sx={{ fontSize: 48 }} />
                                        <Typography>No document selected</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                
                <Divider />
                
                <DialogActions sx={{ p: 3 }}>
                    <AnimateButton>
                        <Button
                            variant="contained"
                            onClick={() => navigate('/admin/tenders')}
                            sx={{ background: BUTTON_CANCEL_COLOR }}
                        >
                            Cancel
                        </Button>
                    </AnimateButton>
                    <AnimateButton>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            sx={{ background: BUTTON_SAVE_COLOR }}
                        >
                            {isSubmitting ? 'Saving...' : isNew ? 'Create' : 'Save'}
                        </Button>
                    </AnimateButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TenderBody;